// src/pages/feasibility/FeasibilityWeather.jsx
// Single home for the weather input of a feasibility study.
//
// Everything weather-related lives here: choosing the source per asset
// (PV / Wind), importing or fetching TMY datasets, and inspecting what the
// chosen dataset actually contains. The PV System setup page and the
// Weather & Generation charts deliberately no longer carry any of this —
// weather is a feasibility input, so it is configured in one place.
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tooltip,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { getChartChrome } from "../../theme";
import {
  Area,
  Bar,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import WbSunnyOutlinedIcon from "@mui/icons-material/WbSunnyOutlined";
import FlareOutlinedIcon from "@mui/icons-material/FlareOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import ThermostatOutlinedIcon from "@mui/icons-material/ThermostatOutlined";
import AirOutlinedIcon from "@mui/icons-material/AirOutlined";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import FeasibilityShell from "./FeasibilityShell";
import WeatherSourceSelector from "../../components/weather/WeatherSourceSelector";
import PvUncertaintyPanel from "../../components/PvUncertaintyPanel";
import { useLanguage } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";
import { useFile } from "../../context/FileContext";
import { useHistory } from "../../context/HistoryContext";
import { DEFAULT_CONFIG } from "../../components/config/defaultConfig";
import {
  downloadPlatformWeather,
  fetchPlatformTypicality,
  fetchPlatformWeatherProfile,
  fetchProjectTmyProfile,
  listProjectTmy,
} from "../../services/api";
import {
  normalizeSimulationYear,
  simulationYears,
} from "../../utils/simulationYears";
import { deriveProjectAssets } from "../../utils/projectAssets";

const MONTH_LABELS = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];

// Sentinel id for the platform pseudo-entry in the inspector. Stored datasets
// are UUIDs, so this cannot collide with one. Module scope, so it is stable
// across renders and safe to name in hook dependency lists.
const PLATFORM_ID = "__platform__";

const COLORS = {
  ghi: "#f59e0b",
  dni: "#ef4444",
  dhi: "#38bdf8",
  temp: "#dc2626",
  wind: "#0ea5e9",
  band: "#fca5a5",
};

function StatTile({ label, value, unit, icon, accent }) {
  return (
    <Paper
      variant="outlined"
      sx={{
        position: "relative",
        overflow: "hidden",
        p: 1.5,
        pl: 2,
        borderRadius: 2,
        height: "100%",
        display: "flex",
        alignItems: "center",
        gap: 1.25,
        transition: "box-shadow 140ms, transform 140ms",
        "&:hover": { boxShadow: 3, transform: "translateY(-1px)" },
        // Colour-coded accent rail, so the tiles read as a set at a glance.
        "&::before": {
          content: '""',
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          flexShrink: 0,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          bgcolor: `${accent}1F`,
          color: accent,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            color: "text.secondary",
            fontWeight: 700,
            letterSpacing: 0.6,
            lineHeight: 1.2,
            whiteSpace: "nowrap",
          }}
        >
          {label}
        </Typography>
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, lineHeight: 1.2, whiteSpace: "nowrap" }}
        >
          {value ?? "—"}
          {value != null && unit ? (
            <Typography
              component="span"
              variant="caption"
              sx={{ ml: 0.5, color: "text.secondary", fontWeight: 600 }}
            >
              {unit}
            </Typography>
          ) : null}
        </Typography>
      </Box>
    </Paper>
  );
}

// Hour-of-day x month mean irradiance. Rendered as a CSS grid of cells rather
// than a chart library: a heatmap needs no axes maths and this keeps 288 cells
// cheap. Seasonality, solar-day phase and any missing block are all visible.
function IrradianceHeatmap({ cells }) {
  const max = cells.reduce((m, c) => Math.max(m, c.ghi ?? 0), 0) || 1;
  const byKey = new Map(cells.map((c) => [`${c.month}-${c.hour}`, c.ghi ?? 0]));
  const shade = (v) => {
    if (!v) return "transparent";
    const t = Math.min(1, v / max);
    // pale amber -> deep orange
    const r = Math.round(255 - 20 * t);
    const g = Math.round(243 - 128 * t);
    const b = Math.round(214 - 200 * t);
    return `rgb(${r},${g},${b})`;
  };
  return (
    <Box sx={{ display: "flex", gap: 0.5, alignItems: "stretch" }}>
      <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "space-between", pr: 0.5 }}>
        {[0, 6, 12, 18, 23].map((h) => (
          <Typography key={h} variant="caption" sx={{ fontSize: 10, color: "text.secondary" }}>
            {String(h).padStart(2, "0")}h
          </Typography>
        ))}
      </Box>
      <Box sx={{ flex: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: "2px" }}>
          {Array.from({ length: 12 }).map((_, mi) => (
            <Box key={mi} sx={{ display: "grid", gridTemplateRows: "repeat(24, 1fr)", gap: "1px" }}>
              {Array.from({ length: 24 }).map((__, h) => {
                const v = byKey.get(`${mi + 1}-${h}`) || 0;
                return (
                  <Tooltip
                    key={h}
                    arrow
                    placement="top"
                    title={`${MONTH_LABELS[mi]} ${String(h).padStart(2, "0")}:00 — ${Math.round(v)} W/m²`}
                  >
                    <Box sx={{ height: 8, bgcolor: shade(v), borderRadius: "1px" }} />
                  </Tooltip>
                );
              })}
            </Box>
          ))}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: "2px", mt: 0.5 }}>
          {MONTH_LABELS.map((m) => (
            <Typography
              key={m}
              variant="caption"
              sx={{ fontSize: 10, textAlign: "center", color: "text.secondary" }}
            >
              {m}
            </Typography>
          ))}
        </Box>
      </Box>
    </Box>
  );
}

function ChartCard({ title, subtitle, height = 260, children }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, height: "100%" }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {subtitle}
        </Typography>
      )}
      <Box sx={{ height, mt: 1.5 }}>
        <ResponsiveContainer width="100%" height="100%">
          {children}
        </ResponsiveContainer>
      </Box>
    </Paper>
  );
}

export default function FeasibilityWeather() {
  // Gridlines, axis annotations and the "context" bars are furniture:
  // baked slate hexes read as glare on a dark page, so they follow the
  // mode. The data series themselves (GHI amber, DNI red...) are
  // identities and stay put.
  const { palette } = useTheme();
  const chartChrome = useMemo(() => getChartChrome(palette.mode), [palette.mode]);

  // Recharts tooltips are unstyled by default: a white card with dark text.
  // In dark mode that reads as a light-mode artefact dropped onto the page and
  // the faint series labels nearly vanish against it. Applied to all seven
  // charts below so none of them can be missed.
  const tooltipStyles = useMemo(
    () => ({
      contentStyle: {
        background: chartChrome.tooltipBg,
        border: `1px solid ${chartChrome.tooltipBorder}`,
        borderRadius: 8,
        color: chartChrome.tooltipText,
        boxShadow: "0 4px 16px rgba(0,0,0,0.28)",
      },
      labelStyle: { color: chartChrome.tooltipLabel, fontWeight: 600 },
      itemStyle: { color: chartChrome.tooltipText },
      // The default cursor is an opaque light band that washes out the bars
      // underneath it on a dark surface.
      cursor: { fill: chartChrome.inactiveBar },
    }),
    [chartChrome]
  );
  const { isGreek } = useLanguage();
  const [weatherTab, setWeatherTab] = useState("reference");
  const { accessToken } = useAuth();
  const { currentProject } = useFile();
  const history = useHistory();
  const projectId = currentProject?.id;

  // Only offer a weather source for generating assets the project actually has.
  // systemType values: "Standalone BESS" | "Hybrid" (PV+BESS) |
  // "Hybrid Wind" (Wind+BESS) | "Hybrid PV Wind".
  const cfg = useMemo(() => history?.present ?? DEFAULT_CONFIG, [history?.present]);
  // Shared with FeasibilitySimulation, which reports the same sources on its
  // Scenario Inputs panel — see utils/projectAssets.
  const { hasPv, hasWind } = useMemo(() => deriveProjectAssets(cfg), [cfg]);

  const selectedTab = hasPv ? weatherTab : "reference";

  const [datasets, setDatasets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);

  const loadDatasets = useCallback(async () => {
    if (!projectId) {
      setDatasets([]);
      return;
    }
    try {
      const data = await listProjectTmy(projectId, accessToken);
      setDatasets(data?.imports || []);
    } catch {
      setDatasets([]);
    }
  }, [projectId, accessToken]);

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets, reloadKey]);

  // The platform's own weather is inspectable too, as a pseudo-entry in the
  // dropdown. Without it a project left on the default source sees charts of a
  // file it is not using, which reads as if those numbers drive the study.
  const platformLat = cfg?.loc_info?.lat ?? null;
  const platformLon = cfg?.loc_info?.lon ?? null;
  const platformTz = cfg?.timeZone ?? null;
  const platformInspectable =
    platformLat != null && platformLon != null && Boolean(platformTz);
  // Declared here rather than beside the other derived values further down:
  // the effects below name it in their dependency arrays, which are evaluated
  // during render, so a later `const` would be read before initialisation.
  const isPlatform = selectedId === PLATFORM_ID;

  // Platform weather is a specific calendar year, so the inspector needs to say
  // which. It opens on the year the study is set to run, from the same list the
  // Simulation page offers, so what you inspect is always a year you can run.
  // Changing it here only changes the preview -- the study's own year is set on
  // the Simulation page, and silently rewriting it from here would be a nasty
  // surprise for someone who just wanted to look at another year.
  const studyYear = normalizeSimulationYear(cfg?.feasibility?.simulationYear);
  const yearOptions = useMemo(() => simulationYears(), []);
  const [platformYear, setPlatformYear] = useState(studyYear);
  useEffect(() => {
    setPlatformYear(studyYear);
  }, [studyYear]);

  // Annual irradiation for every selectable year. A study runs ONE real year,
  // so if that year was dull the whole financial model inherits it and no other
  // chart on this page would show it — each year looks unremarkable alone.
  const [typicality, setTypicality] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!projectId || !isPlatform || !platformInspectable) {
      setTypicality(null);
      return () => {
        alive = false;
      };
    }
    fetchPlatformTypicality(
      {
        projectId,
        lat: platformLat,
        lon: platformLon,
        tz: platformTz,
        years: yearOptions,
      },
      accessToken
    )
      .then((t) => {
        if (alive) setTypicality(t);
      })
      // Deliberately silent: this is context, not the thing the user asked for.
      // Losing it should not put an error banner over charts that loaded fine.
      .catch(() => {
        if (alive) setTypicality(null);
      });
    return () => {
      alive = false;
    };
  }, [
    projectId,
    isPlatform,
    platformInspectable,
    platformLat,
    platformLon,
    platformTz,
    yearOptions,
    accessToken,
  ]);

  const typicalityBars = useMemo(
    () =>
      (typicality?.years || []).map((y) => ({
        ...y,
        name: String(y.year),
        selected: y.year === platformYear,
      })),
    [typicality, platformYear]
  );
  const selectedTypicality = typicalityBars.find((y) => y.selected);

  const [downloading, setDownloading] = useState(false);
  const handlePlatformDownload = useCallback(async () => {
    setDownloading(true);
    setError("");
    try {
      await downloadPlatformWeather(
        {
          projectId,
          lat: platformLat,
          lon: platformLon,
          tz: platformTz,
          year: platformYear,
        },
        accessToken
      );
    } catch (err) {
      setError(err?.message || "Could not download the platform weather.");
    } finally {
      setDownloading(false);
    }
  }, [projectId, platformLat, platformLon, platformTz, platformYear, accessToken]);

  // Default the inspector to whatever drives the study: the active PV source,
  // then wind, then the platform when nothing is assigned, then the newest file.
  useEffect(() => {
    const activePv = datasets.find((d) => d.is_active_pv);
    const activeWind = datasets.find((d) => d.is_active_wind);
    const preferredId =
      activePv?.id ||
      activeWind?.id ||
      (platformInspectable ? PLATFORM_ID : datasets[0]?.id) ||
      "";
    setSelectedId((prev) => {
      const stillValid =
        (prev === PLATFORM_ID && platformInspectable) ||
        datasets.some((d) => d.id === prev);
      return stillValid ? prev : preferredId;
    });
  }, [datasets, platformInspectable]);

  useEffect(() => {
    let alive = true;
    if (!projectId || !selectedId) {
      setProfile(null);
      return () => {
        alive = false;
      };
    }
    setLoading(true);
    setError("");
    const request =
      selectedId === PLATFORM_ID
        ? fetchPlatformWeatherProfile(
            {
              projectId,
              lat: platformLat,
              lon: platformLon,
              tz: platformTz,
              year: platformYear,
            },
            accessToken
          )
        : fetchProjectTmyProfile(projectId, selectedId, accessToken);
    request
      .then((p) => {
        if (alive) setProfile(p);
      })
      .catch((err) => {
        if (alive) {
          setProfile(null);
          setError(
            err?.message ||
              (selectedId === PLATFORM_ID
                ? "Could not summarise the platform weather for this location."
                : "Could not load the dataset profile.")
          );
        }
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [
    projectId,
    selectedId,
    accessToken,
    reloadKey,
    platformLat,
    platformLon,
    platformTz,
    platformYear,
  ]);

  const monthly = useMemo(
    () =>
      (profile?.monthly || []).map((m) => ({
        ...m,
        name: MONTH_LABELS[(m.month || 1) - 1],
        // Recharts cannot draw a band directly, so carry the lower bound and
        // the span; stacking the span on the base renders min..max.
        temp_base: m.temp_min,
        temp_span:
          m.temp_max != null && m.temp_min != null ? m.temp_max - m.temp_min : null,
        diffuse_pct:
          m.ghi_kwh_m2 && m.dhi_kwh_m2 != null
            ? Math.round((100 * m.dhi_kwh_m2) / m.ghi_kwh_m2)
            : null,
      })),
    [profile]
  );

  const daily = useMemo(() => profile?.daily || [], [profile]);
  const hourMonth = useMemo(() => profile?.hour_month || [], [profile]);
  const windHist = useMemo(() => profile?.wind_histogram || [], [profile]);
  const duration = useMemo(() => profile?.ghi_duration || [], [profile]);
  const diurnal = useMemo(() => profile?.diurnal || [], [profile]);
  const annual = profile?.annual;


  // What actually drives each asset, so the inspector can say whether the thing
  // on screen is the thing that runs. No imported dataset active means the
  // platform's own weather is used, which is why "none active" resolves to it.
  const pvDriver = datasets.find((d) => d.is_active_pv)?.id || PLATFORM_ID;
  const windDriver = datasets.find((d) => d.is_active_wind)?.id || PLATFORM_ID;
  const drivesPv = hasPv && selectedId === pvDriver;
  const drivesWind = hasWind && selectedId === windDriver;
  const drivesNothing = Boolean(selectedId) && !drivesPv && !drivesWind;

  // Layout uses plain CSS grid rather than MUI <Grid item xs=...>: this project
  // is on MUI v7, where Grid is the v2 API and `item`/`xs`/`lg` no longer size
  // anything (that API moved to GridLegacy). Under v7 every "xs={12}" item
  // collapsed to auto width, which is what put the first charts alongside the
  // stat tiles and left a large gap beneath them.
  const navigate = useNavigate();
  const twoUp = { xs: "1fr", lg: "1fr 1fr" };
  const full = { gridColumn: "1 / -1" };

  return (
    <FeasibilityShell
      title="Weather Data"
      subtitle="Choose the weather source that drives the feasibility study, and inspect what it contains. An imported TMY dataset replaces the platform's internal weather entirely for the asset it is assigned to."
    >
      {hasPv && <Tabs value={selectedTab} onChange={(_, tab) => setWeatherTab(tab)}
        variant="scrollable" scrollButtons="auto" allowScrollButtonsMobile
        aria-label={isGreek ? "Μετεωρολογικά δεδομένα" : "Weather data"}
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }} data-no-translate>
        <Tab value="reference" id="weather-reference-tab" aria-controls="weather-reference-panel"
          label={isGreek ? "Δεδομένα έτους αναφοράς" : "Reference-year data"} sx={{ textTransform: "none" }} />
        <Tab value="history" id="weather-history-tab" aria-controls="weather-history-panel"
          label={isGreek ? "Πολυετή ιστορικά δεδομένα" : "Multi-year historical data"} sx={{ textTransform: "none" }} />
      </Tabs>}
      {hasPv && <Box role="tabpanel" id="weather-history-panel" aria-labelledby="weather-history-tab"
        hidden={selectedTab !== "history"}>
        <Typography variant="body2" color="text.secondary" data-no-translate>
          {isGreek
            ? "Τα δεδομένα αυτά χρησιμοποιούνται στην Παραγωγή → Πολυετή ανάλυση για τον υπολογισμό P10/P50/P90."
            : "These data are used in Generation → Multi-year analysis to calculate P10/P50/P90."}
        </Typography>
        <PvUncertaintyPanel config={cfg} projectId={projectId} accessToken={accessToken}
          weatherOnly active={selectedTab === "history"}
          onChange={(value) => history.commit({ ...cfg, pvUncertainty: value })}
          weatherAction={<Button variant="outlined" endIcon={<ArrowForwardIcon />} data-no-translate
          onClick={() => navigate("/project/asset?tab=pv&pv=generation&analysis=multiyear")}
          sx={{ textTransform: "none", mb: 1 }}>
          {isGreek ? "Μετάβαση στην πολυετή ανάλυση" : "Go to multi-year analysis"}
        </Button>} />
      </Box>}
      <Box role={hasPv ? "tabpanel" : undefined} id="weather-reference-panel"
        aria-labelledby={hasPv ? "weather-reference-tab" : undefined} hidden={selectedTab !== "reference"}
        sx={{ display: selectedTab === "reference" ? "grid" : "none", gridTemplateColumns: twoUp, gap: 2 }}>
        {/* ---------- Source selection: only for assets in this project ---------- */}
        {hasPv && (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2, ...(hasWind ? {} : full) }}
          >
            <WeatherSourceSelector
              projectId={projectId}
              accessToken={accessToken}
              asset="pv"
              hasPv={hasPv}
              hasWind={hasWind}
              onChanged={() => setReloadKey((k) => k + 1)}
            />
            {/* The yield run reads the source chosen just above, and lives in
                Asset Setup -> PV System -> Generation. It belongs inside this
                panel: it is PV-only, so under both panels it would have looked
                as though it covered wind too. The arrow says the click moves
                you there rather than running anything here. */}
            {projectId ? (
              <Box sx={{ mt: 1.5, pt: 1.25, borderTop: "1px solid", borderColor: "divider" }}>
                <Button
                  variant="outlined"
                  size="small"
                  endIcon={<ArrowForwardIcon fontSize="small" />}
                  onClick={() => navigate("/project/asset?tab=pv&pv=generation")}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Go to the generation run
                </Button>
                <Typography variant="caption" sx={{ display: "block", color: "text.secondary", mt: 0.75 }}>
                  Runs the PV generation model on the source selected here.
                </Typography>
              </Box>
            ) : null}
          </Paper>
        )}
        {hasWind && (
          <Paper
            variant="outlined"
            sx={{ p: 1.5, borderRadius: 2, ...(hasPv ? {} : full) }}
          >
            <WeatherSourceSelector
              projectId={projectId}
              accessToken={accessToken}
              asset="wind"
              hasPv={hasPv}
              hasWind={hasWind}
              onChanged={() => setReloadKey((k) => k + 1)}
            />
          </Paper>
        )}
        {!hasPv && !hasWind && (
          <Alert severity="info" sx={full}>
            This asset has no generation equipment (asset type is{" "}
            <b>{cfg?.systemType || "Standalone BESS"}</b>), so no weather source is
            required. Choose a generation type in <b>Asset Setup → Site &amp; General → Change asset type</b> to configure
            weather.
          </Alert>
        )}

        <Divider sx={{ ...full, my: 0.5 }} />

        {!projectId ? (
          <Alert severity="info" sx={full}>
            Save or open a project to configure its weather input.
          </Alert>
        ) : !datasets.length && !platformInspectable ? (
          <Alert severity="info" sx={full}>
            Set the project coordinates and time zone to inspect the platform's
            internal weather, or use <b>Import TMY</b> above to upload a file (CSV
            template, PVGIS, EPW, SolarGIS or Meteonorm) or fetch a PVGIS typical
            year for this site.
          </Alert>
        ) : (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.5}
              alignItems={{ xs: "flex-start", sm: "center" }}
              sx={full}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                Inspect
              </Typography>
              <Select
                size="small"
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                sx={{ minWidth: 320 }}
              >
                {platformInspectable && (
                  <MenuItem value={PLATFORM_ID}>Platform internal</MenuItem>
                )}
                {datasets.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.original_filename || d.id}
                  </MenuItem>
                ))}
              </Select>
              {/* Year applies to platform weather only: an imported TMY is a
                  typical year and has no calendar year to choose. */}
              {isPlatform && (
                <>
                  <Select
                    size="small"
                    value={platformYear}
                    onChange={(e) => setPlatformYear(Number(e.target.value))}
                    sx={{ minWidth: 120 }}
                  >
                    {yearOptions.map((y) => (
                      <MenuItem key={y} value={y}>
                        {y}
                        {y === studyYear ? " · study year" : ""}
                      </MenuItem>
                    ))}
                  </Select>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    disabled={downloading || loading}
                    onClick={handlePlatformDownload}
                  >
                    {downloading ? "Preparing…" : "Download CSV"}
                  </Button>
                </>
              )}
              {drivesNothing && (
                <Chip
                  size="small"
                  variant="outlined"
                  label="Preview only"
                  sx={{ fontWeight: 700 }}
                />
              )}
              {loading && <CircularProgress size={18} />}
            </Stack>

            {/* The figures below are large and read as authoritative, so say
                plainly when they describe something the study does not use. */}
            {drivesNothing && (
              <Alert severity="info" sx={{ ...full, py: 0.5 }}>
                <Typography variant="body2">
                  These figures describe a dataset this study does not use.{" "}
                  {hasPv && (
                    <>
                      PV runs on{" "}
                      <b>
                        {pvDriver === PLATFORM_ID
                          ? "Platform internal"
                          : datasets.find((d) => d.id === pvDriver)?.original_filename ||
                            "an imported dataset"}
                      </b>
                      .{" "}
                    </>
                  )}
                  {hasWind && (
                    <>
                      Wind runs on{" "}
                      <b>
                        {windDriver === PLATFORM_ID
                          ? "Platform internal"
                          : datasets.find((d) => d.id === windDriver)?.original_filename ||
                            "an imported dataset"}
                      </b>
                      .{" "}
                    </>
                  )}
                  Change that in the Weather source panel above.
                </Typography>
              </Alert>
            )}

            {error && <Alert severity="error" sx={full}>{error}</Alert>}

            {annual && (
              <>
                {/* ---------- Headline figures: own full-width row ---------- */}
                <Box
                  sx={{
                    ...full,
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1.5,
                    "& > *": { flex: "1 1 150px", minWidth: 150 },
                  }}
                >
                  <StatTile
                    label="ANNUAL GHI"
                    value={annual.ghi_kwh_m2}
                    unit="kWh/m²"
                    accent={COLORS.ghi}
                    icon={<WbSunnyOutlinedIcon fontSize="small" />}
                  />
                  <StatTile
                    label="ANNUAL DNI"
                    value={annual.dni_kwh_m2}
                    unit="kWh/m²"
                    accent={COLORS.dni}
                    icon={<FlareOutlinedIcon fontSize="small" />}
                  />
                  <StatTile
                    label="PEAK GHI"
                    value={annual.peak_ghi}
                    unit="W/m²"
                    accent="#7c3aed"
                    icon={<BoltOutlinedIcon fontSize="small" />}
                  />
                  <StatTile
                    label="MEAN TEMP"
                    value={annual.temp_mean}
                    unit="°C"
                    accent={COLORS.temp}
                    icon={<ThermostatOutlinedIcon fontSize="small" />}
                  />
                  <StatTile
                    label="MEAN WIND"
                    value={annual.wind_mean}
                    unit="m/s"
                    accent={COLORS.wind}
                    icon={<AirOutlinedIcon fontSize="small" />}
                  />
                  <StatTile
                    label="HOURS"
                    value={annual.rows}
                    accent={chartChrome.tick}
                    icon={<ScheduleOutlinedIcon fontSize="small" />}
                  />
                </Box>

                {/* Only for platform weather: a TMY is already an average of
                    many years, so "is this year typical" does not apply to it. */}
                {isPlatform && typicalityBars.length > 1 && (
                  <Paper variant="outlined" sx={{ ...full, p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      How typical is this year?
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Annual irradiation for each selectable year. A study runs one
                      real year, so an unusually dull or bright one carries into
                      every year of the financial model.
                    </Typography>
                    <Box sx={{ height: 170, mt: 1.5 }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart
                          data={typicalityBars}
                          margin={{ top: 5, right: 8, bottom: 0, left: -8 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" vertical={false} />
                          <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                          <YAxis
                            tick={{ fontSize: 11 }}
                            domain={["dataMin - 100", "dataMax + 50"]}
                            tickFormatter={(v) => Math.round(v)}
                          />
                          <RTooltip {...tooltipStyles}
                            formatter={(v, _n, p) => [
                              `${v} kWh/m² (${p?.payload?.deviation_pct > 0 ? "+" : ""}${
                                p?.payload?.deviation_pct ?? "—"
                              }% vs mean)`,
                              "Annual GHI",
                            ]}
                          />
                          {typicality?.mean_ghi_kwh_m2 != null && (
                            <ReferenceLine
                              y={typicality.mean_ghi_kwh_m2}
                              stroke={chartChrome.tick}
                              strokeDasharray="4 4"
                              label={{
                                value: `${Math.round(
                                  typicality.mean_ghi_kwh_m2
                                )} mean`,
                                position: "right",
                                fontSize: 10,
                                fill: chartChrome.tick,
                              }}
                            />
                          )}
                          <Bar dataKey="ghi_kwh_m2" radius={[3, 3, 0, 0]}>
                            {typicalityBars.map((y) => (
                              <Cell
                                key={y.year}
                                // The year on screen is solid; the rest are
                                // context, so they recede.
                                fill={y.selected ? COLORS.ghi : chartChrome.inactiveBar}
                              />
                            ))}
                          </Bar>
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                    {selectedTypicality?.deviation_pct != null && (
                      <Typography
                        variant="body2"
                        sx={{
                          mt: 1,
                          fontWeight: Math.abs(selectedTypicality.deviation_pct) >= 3 ? 700 : 400,
                          color:
                            Math.abs(selectedTypicality.deviation_pct) >= 3
                              ? "warning.main"
                              : "text.secondary",
                        }}
                      >
                        {platformYear} received{" "}
                        {selectedTypicality.deviation_pct === 0
                          ? "exactly the five-year mean"
                          : `${Math.abs(selectedTypicality.deviation_pct).toFixed(1)}% ${
                              selectedTypicality.deviation_pct > 0 ? "more" : "less"
                            } irradiation than the five-year mean`}
                        {Math.abs(selectedTypicality.deviation_pct) >= 3
                          ? " — yield modelled on it will carry the same bias."
                          : "."}
                      </Typography>
                    )}
                  </Paper>
                )}

                {/* ---------- Charts ---------- */}
                <ChartCard
                  title="Monthly irradiation"
                  subtitle="Energy received per m² each month — the seasonal shape of the resource."
                >
                  <ComposedChart data={monthly} margin={{ top: 5, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="e" tick={{ fontSize: 11 }} />
                    <YAxis
                      yAxisId="k"
                      orientation="right"
                      domain={[0, 100]}
                      tick={{ fontSize: 11 }}
                      unit="%"
                    />
                    {/* Units live on the axes but not in the tooltip, and this chart
                        mixes two of them (kWh/m² on the left, % on the right). */}
                    <RTooltip {...tooltipStyles}
                      formatter={(value, name) => [
                        value == null
                          ? "—"
                          : name === "Diffuse fraction"
                            ? `${value} %`
                            : `${value} kWh/m²`,
                        name,
                      ]}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar yAxisId="e" dataKey="ghi_kwh_m2" name="GHI" fill={COLORS.ghi} radius={[3, 3, 0, 0]} />
                    <Bar yAxisId="e" dataKey="dni_kwh_m2" name="DNI" fill={COLORS.dni} radius={[3, 3, 0, 0]} />
                    <Bar yAxisId="e" dataKey="dhi_kwh_m2" name="DHI" fill={COLORS.dhi} radius={[3, 3, 0, 0]} />
                    {/* Diffuse fraction = DHI/GHI: high values mean a hazy or
                        cloudy climate, which favours flatter tilts. */}
                    <Line
                      yAxisId="k"
                      dataKey="diffuse_pct"
                      name="Diffuse fraction"
                      stroke={chartChrome.neutralSeries}
                      strokeDasharray="4 3"
                      dot={false}
                      strokeWidth={1.8}
                      connectNulls={false}
                    />
                  </ComposedChart>
                </ChartCard>

                <ChartCard
                  title="Average day profile"
                  subtitle="Mean irradiance by hour of day across the year — shows the solar day and its phase."
                >
                  <ComposedChart data={diurnal} margin={{ top: 5, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="hour" tick={{ fontSize: 11 }} unit="h" />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip {...tooltipStyles} formatter={(v, n) => [`${v} W/m²`, String(n).toUpperCase()]} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Area
                      dataKey="ghi"
                      name="GHI"
                      stroke={COLORS.ghi}
                      fill={COLORS.ghi}
                      fillOpacity={0.25}
                      connectNulls={false}
                    />
                    <Line dataKey="dni" name="DNI" stroke={COLORS.dni} dot={false} strokeWidth={2} connectNulls={false} />
                    <Line dataKey="dhi" name="DHI" stroke={COLORS.dhi} dot={false} strokeWidth={2} connectNulls={false} />
                  </ComposedChart>
                </ChartCard>

                {hourMonth.length > 0 && (
                  <Paper variant="outlined" sx={{ ...full, p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                      Irradiance by hour and month
                    </Typography>
                    <Typography variant="caption" sx={{ color: "text.secondary" }}>
                      Mean GHI for every hour of every month. A clean diagonal bloom is
                      healthy data; a horizontal shift means the solar day is out of
                      phase, and blank patches mean missing hours.
                    </Typography>
                    <Box sx={{ mt: 1.5 }}>
                      <IrradianceHeatmap cells={hourMonth} />
                    </Box>
                  </Paper>
                )}

                <ChartCard
                  title="Daily irradiation through the year"
                  subtitle="One point per day. Gaps or flat runs here are a quick sanity check on data quality."
                  height={240}
                >
                  <ComposedChart data={daily} margin={{ top: 5, right: 8, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis
                      dataKey="day_of_year"
                      tick={{ fontSize: 11 }}
                      ticks={[1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335]}
                      tickFormatter={(d) => {
                        const starts = [1, 32, 60, 91, 121, 152, 182, 213, 244, 274, 305, 335];
                        const i = starts.indexOf(d);
                        return i >= 0 ? MONTH_LABELS[i] : "";
                      }}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <RTooltip {...tooltipStyles}
                      formatter={(v) => [`${v} kWh/m²`, "Daily GHI"]}
                      labelFormatter={(d) => {
                        const row = daily.find((x) => x.day_of_year === d);
                        return row ? `${MONTH_LABELS[row.month - 1]} ${row.day}` : `Day ${d}`;
                      }}
                    />
                    <Line
                      dataKey="ghi_kwh_m2"
                      name="Daily GHI"
                      stroke={COLORS.ghi}
                      dot={false}
                      strokeWidth={1.6}
                      connectNulls={false}
                    />
                  </ComposedChart>
                </ChartCard>

                <ChartCard
                  title="Monthly temperature range and wind"
                  subtitle="Shaded band is the monthly min–max temperature; the line is mean wind speed (right axis)."
                  height={240}
                >
                  <ComposedChart data={monthly} margin={{ top: 5, right: 4, bottom: 0, left: -8 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="t" tick={{ fontSize: 11 }} unit="°C" />
                    <YAxis yAxisId="w" orientation="right" tick={{ fontSize: 11 }} unit=" m/s" />
                    {/* The band is a stacking trick: an invisible base at temp_min
                        plus a visible span of (temp_max - temp_min). A bare tooltip
                        therefore reported the SPAN WIDTH under the label
                        "Temp min-max" - e.g. "23.5" for a month running -2.6..20.9 -
                        and leaked the helper series as a stray "min" row. Read the
                        real bounds off the payload instead, and add the units the
                        axes carry. */}
                    <RTooltip {...tooltipStyles}
                      formatter={(value, name, props) => {
                        const p = props?.payload || {};
                        if (name === "Temp min–max") {
                          return [
                            p.temp_min == null || p.temp_max == null
                              ? "—"
                              : `${p.temp_min} … ${p.temp_max} °C`,
                            name,
                          ];
                        }
                        if (name === "Temp mean") {
                          return [value == null ? "—" : `${value} °C`, name];
                        }
                        if (name === "Wind mean") {
                          return [value == null ? "—" : `${value} m/s`, name];
                        }
                        return [value, name];
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {/* Invisible base + visible span = a min..max band.
                        tooltipType="none" keeps this helper out of the tooltip; it is
                        a rendering device, not a series anyone should read. */}
                    <Area
                      yAxisId="t"
                      dataKey="temp_base"
                      stackId="temp"
                      stroke="none"
                      fill="none"
                      legendType="none"
                      tooltipType="none"
                      name="min"
                    />
                    <Area
                      yAxisId="t"
                      dataKey="temp_span"
                      stackId="temp"
                      stroke="none"
                      fill={COLORS.band}
                      fillOpacity={0.5}
                      name="Temp min–max"
                    />
                    <Line
                      yAxisId="t"
                      dataKey="temp_mean"
                      name="Temp mean"
                      stroke={COLORS.temp}
                      dot={false}
                      strokeWidth={2}
                      connectNulls={false}
                    />
                    <Line
                      yAxisId="w"
                      dataKey="wind_mean"
                      name="Wind mean"
                      stroke={COLORS.wind}
                      dot={false}
                      strokeWidth={2}
                      strokeDasharray="4 3"
                      connectNulls={false}
                    />
                  </ComposedChart>
                </ChartCard>

                {windHist.length > 0 && (
                  <ChartCard
                    title="Wind speed distribution"
                    subtitle="Share of the year spent in each 0.5 m/s band. Wind yield follows the shape of this curve, not the mean."
                    height={240}
                  >
                    <ComposedChart data={windHist} margin={{ top: 5, right: 8, bottom: 0, left: -8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis dataKey="bin" tick={{ fontSize: 11 }} unit=" m/s" interval={3} />
                      <YAxis tick={{ fontSize: 11 }} unit="%" />
                      <RTooltip {...tooltipStyles}
                        formatter={(v, n, item) => [
                          `${v}%  (${item?.payload?.hours ?? 0} h)`,
                          "Share of year",
                        ]}
                        labelFormatter={(b) => `${b}–${(Number(b) + 0.5).toFixed(1)} m/s`}
                      />
                      <Bar dataKey="pct" name="Share of year" fill={COLORS.wind} radius={[2, 2, 0, 0]} />
                    </ComposedChart>
                  </ChartCard>
                )}

                {duration.length > 0 && (
                  <ChartCard
                    title="Irradiance duration curve"
                    subtitle={`Hourly GHI sorted from highest to lowest. Shows how many hours sit above a given level — the top end drives inverter clipping, the bottom end low-light behaviour.${
                      profile?.daylight_hours != null
                        ? ` ${profile.daylight_hours} daylight hours per year.`
                        : ""
                    }`}
                    height={240}
                  >
                    <ComposedChart data={duration} margin={{ top: 5, right: 8, bottom: 0, left: -8 }}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} />
                      <XAxis
                        dataKey="pct"
                        tick={{ fontSize: 11 }}
                        unit="%"
                        ticks={[0, 20, 40, 60, 80, 100]}
                      />
                      <YAxis tick={{ fontSize: 11 }} />
                      <RTooltip {...tooltipStyles}
                        formatter={(v) => [`${v} W/m²`, "GHI exceeded"]}
                        labelFormatter={(pctVal) => `${pctVal}% of the year (~${Math.round((Number(pctVal) / 100) * 8760)} h)`}
                      />
                      <Area
                        dataKey="ghi"
                        name="GHI exceeded"
                        stroke={COLORS.ghi}
                        fill={COLORS.ghi}
                        fillOpacity={0.25}
                        strokeWidth={2}
                        connectNulls={false}
                      />
                    </ComposedChart>
                  </ChartCard>
                )}
              </>
            )}
          </>
        )}
      </Box>
    </FeasibilityShell>
  );
}
